import { Loader2, Search, Calendar, FileText, Check, X } from 'lucide-react';

interface ToolExecutionFeedbackProps {
  tools: {
    id: string;
    name: string;
    status: 'pending' | 'executing' | 'completed' | 'failed';
    result?: string;
  }[];
}

const toolIcons: Record<string, React.ReactNode> = {
  web_search: <Search className="w-4 h-4" />,
  google_calendar: <Calendar className="w-4 h-4" />,
  google_drive: <FileText className="w-4 h-4" />,
  saipos: <img src="/saipos-logo.png" alt="Saipos" className="w-4 h-4 object-contain" />,
};

const toolLabels: Record<string, { executing: string; completed: string }> = {
  web_search: {
    executing: 'Buscando na web...',
    completed: 'Busca concluída'
  },
  'Busca na Web': {
    executing: 'Buscando na web...',
    completed: 'Busca concluída'
  },
  google_calendar: {
    executing: 'Acessando calendário...',
    completed: 'Calendário acessado'
  },
  'Google Calendar': {
    executing: 'Acessando calendário...',
    completed: 'Calendário acessado'
  },
  google_drive: {
    executing: 'Acessando arquivos...',
    completed: 'Arquivos acessados'
  },
  'Google Drive': {
    executing: 'Acessando arquivos...',
    completed: 'Arquivos acessados'
  },
  saipos: {
    executing: 'Consultando Saipos...',
    completed: 'Consulta concluída'
  },
  'Saipos': {
    executing: 'Consultando Saipos...',
    completed: 'Consulta concluída'
  },
  generate_file: {
    executing: 'Gerando arquivo...',
    completed: 'Arquivo gerado'
  },
  file_generation: {
    executing: 'Gerando arquivo...',
    completed: 'Arquivo gerado'
  }
};

export function ToolExecutionFeedback({ tools }: ToolExecutionFeedbackProps) {
  if (!tools || tools.length === 0) return null;

  return (
    <div className="flex justify-start px-6 py-2">
      <div className="w-6 flex-shrink-0" />
      <div className="ml-3 space-y-2">
        {tools.map((tool, index) => {
          const icon = toolIcons[tool.id] || <FileText className="w-4 h-4" />;
          const labels = toolLabels[tool.id] || { executing: 'Executando...', completed: 'Concluído' };
          
          return (
            <div 
              key={`${tool.id}-${index}`}
              className={`flex items-center gap-2 text-sm ${
                tool.status === 'executing' 
                  ? 'text-gray-700' 
                  : tool.status === 'completed'
                  ? 'text-green-600'
                  : tool.status === 'failed'
                  ? 'text-red-600'
                  : 'text-gray-400'
              }`}
            >
              {tool.status === 'executing' ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              ) : tool.status === 'completed' ? (
                <Check className="w-4 h-4" />
              ) : tool.status === 'failed' ? (
                <X className="w-4 h-4" />
              ) : (
                icon
              )}
              <span className="font-medium">
                {tool.status === 'executing' 
                  ? labels.executing 
                  : tool.status === 'completed'
                  ? labels.completed
                  : tool.status === 'failed'
                  ? `Erro: ${tool.name}`
                  : tool.name}
              </span>
              {tool.status === 'executing' && (
                <div className="flex gap-0.5">
                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Componente simples para mostrar loading de uma ferramenta
export function SingleToolLoading({ toolId, toolName }: { toolId: string; toolName?: string }) {
  const labels = toolLabels[toolId] || { executing: 'Executando...', completed: 'Concluído' };
  
  return (
    <div className="flex justify-start px-6 py-2">
      <div className="w-6 flex-shrink-0" />
      <div className="ml-3">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
          <span className="font-medium">{toolName || labels.executing}</span>
          <div className="flex gap-0.5">
            <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

